import React, { useState } from "react";

import type { CalendarType } from "./CalendarPicker";
import { CalendarPicker } from "./CalendarPicker";

interface SummaryValue {
    label: string;
    value: string | number;
    color: string;
    unit?: string;
}

interface HealthSummaryProps<T = unknown, M = unknown> {
    filteredData: T[];
    metadata: M;
    handlePrevious: () => void;
    handleNext: () => void;
    onDateSelect: (date: Date) => void;
    selectedDate: Date;
    selectedRange: string;
    getSummaryValues: (metadata: M, filteredData: T[]) => SummaryValue[];
}

export const HealthSummary = <T = unknown, M = unknown>({
    filteredData,
    metadata,
    handlePrevious,
    handleNext,
    onDateSelect,
    selectedDate,
    selectedRange,
    getSummaryValues,
}: HealthSummaryProps<T, M>) => {
    const [showCustomCalendar, setShowCustomCalendar] = useState(false);


    // Helper function to determine calendar type based on selected range
    const getCalendarType = (range: string): CalendarType => {
        switch (range) {
            case "1d":
                return "day";
            case "7d":
                return "week";
            case "1m":
                return "month";
            case "6m":
                return "6m";
            case "1y":
                return "year";
            default:
                return "day";
        }
    };

    const summaryValues = getSummaryValues(metadata, filteredData);

    return (
        <div className="mb-4 px-4">
            <div className="mb-6 flex justify-center space-x-8">
                {summaryValues.map((value: SummaryValue, index: number) => (
                    <div key={index} className="text-center">
                        <div className="mb-1 flex items-center justify-center space-x-2">
                            <div
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: value.color }}
                            ></div>
                            <span className="text-sm text-gray-400">{value.label}</span>
                        </div>
                        <div className="text-2xl font-light">
                            {filteredData?.length > 0 ? value.value : "--"}{" "}
                            {value.unit && (
                                <span className="text-sm text-gray-400">{value.unit}</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mb-4 flex items-center justify-center space-x-4">
                {/* Previous Button */}
                <button
                    onClick={handlePrevious}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
                    title="Previous"
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M15 18L9 12L15 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                {/* Date Display */}
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => setShowCustomCalendar(true)}
                        className="rounded-lg px-4 py-2 text-sm font-medium text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
                    >
                        {selectedDate?.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                        }) ?? "Invalid Date"}
                    </button>
                </div>

                {/* Next Button */}
                <button
                    onClick={handleNext}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-white"
                    title="Next"
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M9 18L15 12L9 6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>

            {/* Calendar Picker Modal */}
            {showCustomCalendar && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
                    onClick={() => setShowCustomCalendar(false)}
                    onKeyDown={(e) => e.key === 'Escape' && setShowCustomCalendar(false)}
                    role="button"
                    tabIndex={0}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                    >
                        <CalendarPicker
                            key={`calendar-${selectedDate?.getTime() ?? 0}-${selectedRange}`}
                            type={getCalendarType(selectedRange)}
                            selectedDate={selectedDate}
                            onDateSelect={(date) => {
                                onDateSelect(date);
                                setShowCustomCalendar(false);
                            }}
                            isOpen={showCustomCalendar}
                            onClose={() => setShowCustomCalendar(false)}
                            inModal={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
